import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchSpacePost } from '@core/actions/space/posts';
import { tryToSubscribeToSpaceLevelEvents } from '@core/actions/socket';
import { getPostById } from '@core/selectors/posts.selectors';
import { getComments } from '@core/selectors/comments';
import CommentsEntityTypes from '@core/utils/commentsEntityTypes';
import { accessingSpaceAsStaff } from '@core/selectors/classroom';
import { operations as reactionOperations } from '@app/store/reactions';
import {
  HOME_UPDATE_POST_COUNTS,
  HOME_REMOVE_POST,
} from '@core/constants/actions';
import fetchPostById from '@core/api/posts/fetchPostById';

import {
  getFullName,
  getUserInitials,
  selectIsEducatorPost,
} from './recentUpdates.utils';
import PostDetailPopupView from './PostDetailPopupView';

export interface PostDetailPopupProps {
  item: any;
  open: boolean;
  onClose: () => void;
}

const PostDetailPopup: React.FC<PostDetailPopupProps> = ({
  item,
  open,
  onClose,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [showComments, setShowComments] = useState(false);

  const spaceId: string = item?.classRoom;
  const postId: string = item?.objectId || item?.id;

  const post = useSelector((state: any) =>
    postId ? getPostById(state, postId) : null
  );

  const comments = useSelector((state: any) =>
    postId ? getComments(state, postId, CommentsEntityTypes.post) : []
  );

  const isReadOnly = useSelector((state: any) =>
    spaceId ? accessingSpaceAsStaff(state)(spaceId) : false
  );

  const reactionsFromStore = useSelector((state: any) => {
    if (!postId) return [];
    const reactionMap = state.data?.reactions || {};
    return Object.values(reactionMap).filter((r: any) => r.itemId === postId);
  });

  const classRoomsMap = useSelector(
    (state: any) => state.data?.classRooms || {}
  );
  const matchedClassRoom = Object.values(classRoomsMap).find(
    (room: any) =>
      room.objectId === item?.classRoom || room.id === item?.classRoom
  ) as any;
  const classRoomName: string | undefined = matchedClassRoom?.name;
  const classRoomStartDate: string | undefined = matchedClassRoom?.startDate;
  const classRoomEndDate: string | undefined = matchedClassRoom?.endDate;

  useEffect(() => {
    if (open && postId && spaceId) {
      setIsLoading(true);
      const loadPost = async () => {
        try {
          const result = await dispatch(fetchSpacePost(postId, true) as any);
          // fetchSpacePost catches errors internally and never re-throws.
          // On success it returns the post object; on error it returns undefined.
          if (!result || !result.objectId) {
            // The fetch failed — check if it was a 404 (post deleted)
            try {
              await fetchPostById(postId);
            } catch (verifyErr: any) {
              const status = verifyErr?.response?.status || verifyErr?.status;
              if (status === 404) {
                dispatch({ type: HOME_REMOVE_POST, postId });
                onClose();
                return;
              }
            }
          } else {
            dispatch(tryToSubscribeToSpaceLevelEvents(spaceId) as any);
          }
        } catch (err: any) {
          console.error('Failed to load post details', err);
        } finally {
          setIsLoading(false);
        }
      };
      loadPost();
    }
  }, [open, postId, spaceId, dispatch, onClose]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    if (postId) {
      const commentsCount = comments?.length || 0;
      const reactionsCount = reactionsFromStore.reduce(
        (sum: number, r: any) =>
          sum + (typeof r.count === 'number' ? r.count : 1),
        0
      );
      // Sync full post data (content, files, etc.) back to the home feed
      const postData = post
        ? {
            content: post.content,
            title: post.title,
            feedType: post.feedType,
            updatedAt: post.updatedAt,
          }
        : undefined;
      dispatch({
        type: HOME_UPDATE_POST_COUNTS,
        postId,
        commentsCount,
        reactionsCount,
        postData,
      });
    }
    setShowComments(false);
    onClose();
  }, [postId, post, comments, reactionsFromStore, dispatch, onClose]);

  const toggleReaction = useCallback(
    ({ emojiName }: { emojiName: string }) =>
      dispatch(
        reactionOperations.toggleReaction({
          emojiName,
          itemId: postId,
          itemType: 'UBClassRoomPost',
        })
      ),
    [dispatch, postId]
  );

  const handleDidComment = useCallback(() => setShowComments(true), []);

  // ── Derived display values ─────────────────────────────────────────────────
  const fullName = getFullName(item?.user);
  const avatarUrl = item?.user?.avatar || '';
  const userInitials = getUserInitials(item?.user);
  const isEducatorPost = useSelector(selectIsEducatorPost(item));

  return (
    <PostDetailPopupView
      open={open}
      onClose={handleClose}
      item={item}
      post={post}
      comments={comments}
      isLoading={isLoading}
      isReadOnly={isReadOnly}
      showComments={showComments}
      fullName={fullName}
      avatarUrl={avatarUrl}
      userInitials={userInitials}
      classRoomName={classRoomName}
      classRoomStartDate={classRoomStartDate}
      classRoomEndDate={classRoomEndDate}
      isEducatorPost={isEducatorPost}
      toggleReaction={toggleReaction}
      onDidComment={handleDidComment}
    />
  );
};

export default PostDetailPopup;
